/**
 * Traduce el fallo técnico de una petición al mensaje que el distribuidor debe leer.
 *
 * ── POR QUÉ EXISTE ───────────────────────────────────────────────────────────────────────
 * Hasta el 2026-09-06, un bache de señal en el celular le mostraba al distribuidor:
 *
 *     "No se pudo conectar al servidor. ¿Está corriendo el API? (ej: uvicorn api.main:app
 *      --port 8000)"
 *
 * Un distribuidor que lee eso concluye que el sistema está roto y levanta un reporte. Pasó de
 * verdad: se investigó como caída del OFV y el API llevaba nueve horas sano — la petición
 * nunca había salido del teléfono. Además el texto filtra detalle de infraestructura a quien
 * no debe verlo.
 *
 * ── EL CRITERIO ──────────────────────────────────────────────────────────────────────────
 * Cada mensaje dice **qué pasó** y **qué hacer**, y no afirma nada que no se pueda saber desde
 * el navegador. En particular NO se dice "revisa tu internet" cuando el navegador se reporta
 * conectado: sería culpar al usuario de algo que quizá no es suyo. La distinción la da
 * `navigator.onLine`, que es lo único que el front sabe de verdad.
 *
 * ── ALCANCE REAL, HOY ────────────────────────────────────────────────────────────────────
 * Adoptado en el login y en las pantallas donde el distribuidor mueve dinero: depósitos
 * (`useDepositsState.ts`, las 5 salidas de error) y asignación de SIMs por archivo
 * (`SimsPage.tsx`). Siguen sin cubrir ~25 sitios en otros archivos que asignan
 * `response.data.detail` directo al aviso; son pantallas de menor exposición y quedan como
 * trabajo aparte. Al leer un `catch` con la forma `detail || "fallback"`, asume que la jerga
 * del servidor todavía llega a pantalla ahí.
 *
 * Dos cosas que este módulo NO puede cerrar por sí solo, y que hay que mirar en cada pantalla:
 *   1. Los fallos que el backend responde con **HTTP 200** (`{success:false, message}`) no
 *      pasan por ningún `catch` — el aviso lo escribe quien llama. Era el caso del timbrado en
 *      `aprobarFactura`, por donde salía un `[Errno 111]` sin tocar este archivo.
 *   2. `textoDelServidor` se enciende **por endpoint comprobado**, no por comodidad: donde el
 *      backend hace `detail=str(e)` hay que dejarlo apagado.
 *
 * ── AL REUSARLO FUERA DEL LOGIN ──────────────────────────────────────────────────────────
 * Pasa `{ esLogin: true }` sólo desde la pantalla de acceso. En cualquier otra, un 401 es una
 * sesión expirada, no una contraseña mal escrita, y mandar a alguien a reescribir su
 * contraseña a media venta es peor que no decir nada.
 *
 * Nota: hoy esa rama es **preventiva**. El endpoint de acceso devuelve HTTP 200 con
 * `data.valido = false` para credenciales malas (`api/routers/auth.py`), así que el 401 casi
 * nunca se dispara en el login. Se deja porque el día que el backend use la semántica correcta,
 * el mensaje ya es el que toca.
 */

/** Forma mínima de un error de axios; evita importar el tipo y acoplar este módulo. */
type ErrorHttp = {
  response?: { status?: number; data?: { detail?: unknown; message?: unknown } }
  code?: string
  message?: string
  config?: { url?: string }
}

type Opciones = {
  esLogin?: boolean
  /**
   * Mostrar el texto que manda el servidor. **Apagado por defecto, y a propósito.**
   *
   * Enciéndelo sólo en pantallas donde hayas comprobado que ese endpoint devuelve mensajes
   * escritos para una persona. Los filtros de abajo siguen actuando como red, pero son
   * best-effort: la garantía real es este interruptor.
   */
  textoDelServidor?: boolean
}

const esErrorHttp = (e: unknown): e is ErrorHttp =>
  typeof e === "object" && e !== null && "isAxiosError" in e

/**
 * Jerga que jamás debe llegar a la pantalla de un distribuidor.
 *
 * NO es paranoia: el backend emite `detail=str(e)` y `detail=f"...{exc}"` en decenas de sitios
 * (`stp.py`, `facturacion.py`, `perfiles_permiso.py`, `venta_externa_service.py`,
 * `contenido.py`). Un `str(e)` de SQLAlchemy o un `KeyError` interno viajaría intacto hasta el
 * aviso en pantalla. Confiar en que el servidor sólo manda texto de negocio sería reabrir por
 * detrás justo la fuga que este módulo cierra por delante.
 */
const JERGA = new RegExp([
  // repr de excepciones y librerías del backend
  "uvicorn|traceback|sqlalchemy|psycopg|asyncpg",
  "keyerror|typeerror|valueerror|attributeerror|nonetype",
  // jerga de RED de las librerías: son frases perfectas, sin ningún término de arriba, y
  // atravesaban todo. Salen de `detail=str(exc)` sobre llamadas a STP y a Altán.
  "errno|certificate verify|connectionpool|max retries|timed out|connection refused",
  "econn|enotfound|etimedout|ssl:|handshake",
  // rastros de infraestructura
  "127\\.0\\.0\\.1|localhost|:\\d{4}\\b|\\bcolumn\\b|\\btable\\b|<[a-z]+ object at 0x",
].join("|"), "i")

/**
 * Un mensaje escrito para una persona es una FRASE, no un identificador.
 *
 * `str(e)` de Python no incluye el nombre de la clase: `str(KeyError('wallet1'))` es
 * `"'wallet1'"`, sin la palabra "KeyError". La denylist de arriba defiende contra el *repr* y
 * contra los mensajes largos de las librerías, pero no contra un identificador suelto — que es
 * lo que el backend acaba mandando.
 *
 * Dos palabras bastan: hay mensajes de negocio legítimamente cortos ("Nombre requerido",
 * "Correo inválido") y tragárselos le quitaría al usuario justo el dato con el que corregir.
 */
const pareceFrase = (d: string): boolean => d.trim().split(/\s+/).length >= 2

/**
 * El patrón DOMINANTE del backend no es `detail=str(e)` pelado, sino
 * `detail=f"<frase de negocio>: {str(e)}"` — y esa forma atraviesa los dos filtros anteriores.
 *
 * `"Error al procesar archivo: 'wallet1'"` tiene cinco palabras y ningún término prohibido, así
 * que pasaría entera **con la cola críptica incluida**. Una denylist siempre llega tarde a este
 * juego. La defensa que sí escala es estructural: **quedarse con la frase y tirar la cola**.
 *
 * Se parte por el último `:` y se descarta lo de después cuando no parece lenguaje —
 * entrecomillado, un identificador con guion bajo, algo sin espacios. Lo que queda es la parte
 * que el programador escribió a propósito para que alguien la lea.
 */
const sinColaTecnica = (d: string): string => {
  // Por el PRIMER `:`, no el último: los prefijos de negocio del backend no llevan dos puntos,
  // pero las horas sí — cortando por el último, "Tu ficha vence a las 23:59" se quedaba en
  // "...a las 23". Y sólo se corta ante un identificador inequívoco; ver abajo.
  const i = d.indexOf(":")
  if (i <= 0 || i === d.length - 1) return d

  const cabeza = d.slice(0, i).trim()
  const cola = d.slice(i + 1).trim()

  // Si la cabeza deja un paréntesis abierto, ese `:` está DENTRO de un inciso y no separa
  // prefijo de detalle: cortar ahí mutila la frase. Pasa con un mensaje real del backend —
  // "…pueden aprobarse (estado actual: pendiente_aprobacion)" quedaba en "…(estado actual".
  if ((cabeza.match(/\(/g) || []).length > (cabeza.match(/\)/g) || []).length) return d

  // Sólo se corta ante señales inequívocas de identificador: entrecomillado, guion bajo, un
  // número largo. Una palabra suelta NO basta — "Tipo inválido: imagen" es exactamente el dato
  // con el que el usuario corrige, y decapitarlo se lo quita.
  // El `_` va ANCLADO a un token suelto. Sin anclar, cualquier guion bajo en mitad de una
  // frase la decapitaba: "Solo solicitudes pendientes pueden aprobarse (estado actual:
  // pendiente_aprobacion)" se quedaba en "...(estado actual", perdiendo justo el estado que el
  // usuario necesita leer.
  const colaEsTecnica = /^['"`]|^\S*_\S*$|^\d{6,}$/.test(cola) || JERGA.test(cola)

  return colaEsTecnica && pareceFrase(cabeza) ? cabeza : d
}

/**
 * El mismo filtro, expuesto para el texto que NO viene de un `catch`.
 *
 * Hay fallos que el backend responde con **HTTP 200** y `{success:false, message}` — el
 * timbrado de una factura es el caso vivo. Ésos no lanzan, así que no pasan por
 * `mensajeDeError` ni por ningún filtro: quien llama escribe el aviso a mano y lo que traiga
 * `message` sale intacto. Por ahí salía a la pantalla del distribuidor un
 * `"[Errno 111] Connection refused"` del PAC.
 *
 * Devuelve `null` cuando el texto no sobrevive al filtro, para que quien llama ponga el suyo.
 * Es deliberado que devuelva algo en vez de tragárselo siempre: el PAC también rechaza por
 * motivos que el usuario SÍ puede corregir ("RFC del receptor no válido"), y esconderlos le
 * quitaría justo el dato con el que arreglarlo.
 */
/**
 * El texto del servidor — sólo con permiso explícito, y aun así filtrado.
 *
 * ── POR QUÉ EL DEFAULT ES NO MOSTRARLO ───────────────────────────────────────────────────
 * Tres rondas de auditoría demostraron que **filtrar texto libre con listas no converge**:
 * cada versión atrapaba lo que miraba y dejaba pasar lo de al lado. Cayeron, una tras otra,
 * `str(KeyError)` sin el nombre de la clase (`"'wallet1'"`), el patrón `f"frase: {str(e)}"`
 * con la cola pegada, y los errores de red de las librerías (`"[Errno 111] Connection
 * refused"`, `"certificate verify failed"`), que son frases en toda regla y no contienen
 * ninguna palabra prohibida.
 *
 * La conclusión no es una lista más larga: es que **la lista siempre llega tarde**. Así que el
 * default se invierte — no se muestra nada del servidor salvo que quien llama lo pida, porque
 * él sí sabe qué devuelve su endpoint. Un mensaje nuestro siempre es comprensible; un
 * `[Errno 111]` en la pantalla de un distribuidor no le sirve a nadie.
 */
export function textoParaPantalla(delServidor: unknown): string | null {
  if (typeof delServidor !== "string" || delServidor.trim() === "") return null

  const d = sinColaTecnica(delServidor.trim())

  return JERGA.test(d) || !pareceFrase(d) ? null : d
}

const detalleDelServidor = (e: ErrorHttp, permitido: boolean): string | null =>
  permitido ? textoParaPantalla(e.response?.data?.detail ?? e.response?.data?.message) : null

/**
 * @param error   lo que atrapó el catch
 * @param accion  qué se estaba intentando, en infinitivo ("iniciar sesión", "guardar la venta")
 * @param opts    `esLogin` cambia el texto del 401
 */
export function mensajeDeError(error: unknown, accion = "completar la operación", opts: Opciones = {}): string {
  // Un Error de JS que no viene de la petición (TypeError, un fallo de red del propio
  // navegador...) trae un mensaje escrito para un programador —"connect ECONNREFUSED
  // 127.0.0.1:8000"— y NUNCA para el distribuidor. Se registra en consola para poder
  // depurarlo, pero lo que se muestra es nuestro.
  if (!esErrorHttp(error)) {
    if (error instanceof Error) {
      console.error("[error no-HTTP]", error)
    }

    return `No se pudo ${accion}. Inténtalo de nuevo y, si continúa, repórtalo.`
  }

  // Rastro para soporte: sin esto, un reporte de "me dice que no pueden comunicarse con el
  // servidor" llega sin un solo dato con el que distinguir un bache de señal de una caída real
  // — que es exactamente la investigación que originó este módulo.
  //
  // La ruta va ENMASCARADA. Varios endpoints llevan el identificador en el path
  // (`/sims/{iccid}`, `/usuarios/{id}`), y un ICCID o un número de línea son datos personales:
  // no tienen por qué acabar en la consola del distribuidor —ni en los eventos que captura el
  // puente de depuración— para distinguir un bache de una caída. La plantilla basta.
  console.warn("[error-http]", {
    status: error.response?.status ?? null,
    code: error.code ?? null,
    ruta: (error.config?.url ?? "")
      .replace(/\/[0-9]{6,}[A-Fa-f]?(?=\/|\?|$)/g, "/:id")
      // También en la query: varios endpoints llevan ahí el identificador
      // (`?iccid=`, `?msisdn=`, `?id_usuario=`), y sin esto salían íntegros.
      .replace(/([?&](?:iccid|msisdn|linea|telefono|email|correo|id_usuario|usuario)=)[^&]*/gi, "$1:x"),
  })

  // La app canceló la petición ella misma (se desmontó el componente, el usuario navegó, un
  // AbortController de React Query). No es un fallo y no debe avisarse como tal.
  if (error.code === "ERR_CANCELED") return ""

  // La petición se agotó por tiempo. No es lo mismo que no haber salido: el servidor pudo
  // haberla recibido, así que se avisa antes de que el usuario repita una operación que quizá
  // ya se aplicó.
  if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
    return "El servidor tardó demasiado en responder. Espera un momento y vuelve a intentarlo; "
      + "si la operación ya se había registrado, no la repitas sin revisar antes."
  }

  // Sin respuesta: la petición no llegó a ningún lado. Es el caso del bache de señal.
  if (!error.response) {
    const sinInternet = typeof navigator !== "undefined" && navigator.onLine === false

    return sinInternet
      ? "Tu dispositivo no tiene conexión a internet. Revisa tus datos o el WiFi e inténtalo de nuevo."
      : "No pudimos comunicarnos con el servidor. Suele ser una falla momentánea de la conexión: "
        + "espera unos segundos y vuelve a intentarlo. Si sigue igual, repórtalo."
  }

  const status = error.response.status ?? 0
  const delServidor = detalleDelServidor(error, opts.textoDelServidor === true)

  if (status === 401) {
    return opts.esLogin
      ? "Usuario o contraseña incorrectos."
      : "Tu sesión expiró. Vuelve a entrar para continuar."
  }
  if (status === 403) return "Tu cuenta no tiene permiso para esta operación."
  if (status === 404) return `No encontramos lo que buscabas al ${accion}.`
  if (status === 409) return delServidor ?? "Esa operación choca con algo que ya existe. Revisa antes de repetirla."
  if (status === 422) return delServidor ?? "Revisa los datos: hay algo que no es válido."
  if (status === 429) return "Demasiados intentos seguidos. Espera un minuto y vuelve a intentarlo."

  // 5xx: es nuestro, y se dice. El distribuidor no tiene nada que arreglar de su lado, así que
  // pedirle que revise algo sería mandarlo a perder el tiempo. Y NUNCA se usa el texto del
  // servidor aquí: un 500 es justo donde el backend manda el `str(e)` de la excepción.
  if (status >= 500) {
    return "Tuvimos un problema en el servidor. No es algo que puedas resolver desde aquí: "
      + "vuelve a intentarlo en unos minutos y, si continúa, repórtalo."
  }

  return delServidor ?? `No se pudo ${accion}. Inténtalo de nuevo.`
}
